import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { declOfNum, removeEmpty, useOrdering, usePagination } from "@helpers/utils";
import { Customer } from "@/types/general";
import BaseAdminFilter from "@reusables/BaseAdminFilter";
import BaseTable from "@reusables/BaseTable";
import BaseTablePaginationControls from "@reusables/BaseTablePaginationControls";
import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import DeletionModal from "../components/modals/DeletionModal";
import { AbilityContext, Can } from "@/casl.config";
import { useAbility } from "@casl/react";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import { useGetCustomersFullQuery } from "@redux/features/customers/customersApi";
import { useGetFileInfoMutation } from "@/redux/features/importApi";
import { isErrorWithMessage } from "@/redux/api/query";
import { useAppDispatch } from "@/redux/hooks";
import { setImportData, setImportFile } from "@redux/features/importInfo/importInfoSlice";
import _ from "lodash";
import BasePermissionBlocker from "@reusables/BasePermissionBlocker";
import IntegrationsBadge from "../components/IntegrationsBadge";
import { toast } from "react-toastify";
import CustomerImportModal from "@components/Dashboard/pages/Customers/Customer/MainPage/parts/CustomerImportModal";
import PowerOfficeRecordSyncButton from "@ready/PowerOfficeRecordSyncButton";
import TwentyFourSevenOfficeRecordSyncButton from "@ready/TwentyFourSevenOfficeRecordSyncButton";

export default function CustomersPage() {
    const history = useHistory();
    const { t } = useTranslation("", { keyPrefix: "customers.main" });

    const ability = useAbility(AbilityContext);
    const dispatch = useAppDispatch();

    // =========== FILTERS RELATED =========== //
    const [searchingValue, setSearchingValue] = useState<string>();
    const searchInputDebounce = useCallback(_.debounce(setSearchingValue, 1000), []);

    const { orderBy, setOrderBy } = useOrdering<Customer.DTO.OrderBy>({ name: "id", type: "desc" });

    const pagination = usePagination({ storageKey: "customers.main" });

    const filters = removeEmpty({
        search: searchingValue
    });

    const { data: customers, isLoading: isCustomersLoading } = useGetCustomersFullQuery({
        filters,
        orderBy,
        pagination: pagination.request
    });

    const [getFileInfo] = useGetFileInfoMutation();
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);

    // const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

    const [actionEntity, setActionEntity] = useState<Customer.Root>();
    const [isDeletionModalOpen, setIsDeletionModalOpen] = useState<boolean>(false);

    return (
        <>
            {/* FILTERS BLOCK */}
            <BaseAdminFilter
                permissionModule={"customer"}

                handleSearch={searchInputDebounce}

                handleImport={file => {
                    if (!file)
                        return;

                    const formData = new FormData();
                    formData.append("file", file);

                    getFileInfo(formData)
                        .unwrap()
                        .then((data) => {
                            dispatch(setImportData(data));
                            dispatch(setImportFile(file));
                            setIsImportModalOpen(true);
                        })
                        .catch((err) => {
                            if (isErrorWithMessage(err)) {
                                toast.error(err.message);
                            } else {
                                console.log(err);
                            }
                        });
                }}
                // handleExport={() => setIsExportModalOpen(true)}
                handleCreate={() => history.push("/dashboard/customers/new")}
            />

            <div className="levitation-extended mt-[32px]">
                <Can not I="view" a="customer">
                    <NoPermissionBanner />
                </Can>

                <Can I="view" a="customer">
                    <div className="mb-[24px] flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        {
                            <div
                                className="bold-highlight capitalize">{customers?.meta.total} {declOfNum(customers?.meta.total ?? 0, [t("table.titleDeclinations.0"), t("table.titleDeclinations.1"), t("table.titleDeclinations.2")])}
                            </div>
                        }
                        <div className="flex items-center gap-2">
                            <PowerOfficeRecordSyncButton entity="customers" />
                            <TwentyFourSevenOfficeRecordSyncButton entity="customers" />
                        </div>
                    </div>

                    <BaseTable
                        data={customers?.payload ?? []}
                        isDataLoading={isCustomersLoading}

                        columns={[
                            {
                                header: t("table.columns.customerNameOrId"),
                                getter: row => {
                                    const basicLayout = <>
                                        <div className={"flex flex-row items-center space-x-2"}>
                                            <div className="bold-highlight">{row.name}</div>
                                            <IntegrationsBadge row={row} />
                                        </div>
                                        <div className="text-sm text-accent">{row.code}</div>
                                    </>;
                                    return <>
                                        <Can I="view" a="customer">
                                            <div
                                                className="hover:opacity-70 transition-[.15s] cursor-pointer"
                                                onClick={() => history.push(`/dashboard/customers/${row.id}/details`)}
                                            >
                                                {basicLayout}
                                            </div>
                                        </Can>

                                        <Can not I="view" a="customer">
                                            <div>
                                                {basicLayout}
                                            </div>
                                        </Can>
                                    </>;
                                },
                                comparator: () => 0
                            },
                            {
                                header: t("table.columns.customerType"),
                                getter: row => row?.is_person ? `${t("table.person")}` : `${t("table.company")}`
                            },
                            {
                                header: t("table.columns.email"),
                                getter: row => row.contacts[0]?.email ?? "-",
                                comparator: () => 0
                            },
                            {
                                header: t("table.columns.phone"),
                                getter: row => row.contacts[0]?.phone ?? "-",
                                comparator: () => 0
                            },
                            {
                                header: t("table.columns.contactName"),
                                getter: row => row.contacts[0]?.name ?? "-",
                                comparator: () => 0
                            },
                            {
                                header: <div className="text-center">{t("table.columns.action")}</div>,
                                getter: (row) =>                                 <div className="text-center space-x-[12px]">
                                    <BasePermissionBlocker action="edit" module="customer">
                                        <EditSVG
                                            className={`text-tables-secondaryIcon ${ability.can("edit", "customer") ? "hover:text-tables-highlightedIcon cursor-pointer" : "cursor-default"}`}
                                            onClick={() => ability.can("edit", "customer") && history.push(`/dashboard/customers/${row.id}/edit`)}
                                            data-testid="customers-action-edit"
                                        />
                                    </BasePermissionBlocker>

                                    <BasePermissionBlocker action="delete" module="customer">
                                        <TrashSVG
                                            className={`text-tables-secondaryIcon ${ability.can("delete", "customer") ? "hover:text-tables-highlightedIcon cursor-pointer" : "cursor-default"}`}
                                            onClick={() => {
                                                ability.can("delete", "customer") && (
                                                    setActionEntity(row),
                                                        setIsDeletionModalOpen(true)
                                                );
                                            }}
                                            data-testid="customers-action-delete"
                                        />
                                    </BasePermissionBlocker>
                                </div>
                            }
                        ]}

                        alternate
                        manualControls={{
                            ordering: (newOrdering) => {
                                if (newOrdering) {
                                    let name: Customer.DTO.OrderBy | undefined;

                                    switch (newOrdering?.index) {
                                        case 0:
                                            name = "customer_name";
                                            break;
                                        case 1:
                                            name = "email";
                                            break;
                                        case 2:
                                            name = "phone";
                                            break;
                                        case 3:
                                            name = "contact_name";
                                            break;
                                        default:
                                            name = undefined;
                                    }

                                    if (name)
                                        setOrderBy({
                                            name,
                                            type: newOrdering.order
                                        });
                                } else {
                                    setOrderBy(undefined);
                                }
                            }
                        }}
                    />

                    <BaseTablePaginationControls
                        className="mt-[32px]"
                        pagination={pagination}
                        total={customers}
                    />
                </Can>
            </div>

            {/* <MainExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                pagination={pagination}
            /> */}

            <CustomerImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
            />

            {/* DELETION MODAL */}
            <DeletionModal
                isOpen={isDeletionModalOpen}
                onClose={() => setIsDeletionModalOpen(false)}
                entity={actionEntity}
            />
        </>
    );
}
